<template>
  <div class="py-2 my-2 help-container">
    <h1 class="separator py-4">CALCULATIONS</h1>
    <span class="banner py-3 mb-4">
      <div>There are six types of calculations or formulas. Each one is based on a different set of criteria allowing you a wide range of flexibility.</div>
      <div>Calculations will be stored once created, even if you have changed the numbers since the calculation was created or added another calculation. You will have an option on the GL Page to view the two most recent calculations that were performed on that GL Account.</div>
      <div>Calculations will also be displayed when entering your GL Assumption statement as it may be helpful to you in creating the Assumption wording.</div>
    </span>

    <!--Help Sections-->
    <v-expansion-panels v-model="openSectionInternal">
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(0)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(0)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Budgeted GL Account</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>Allows you to take another GL Account's existing current year budget and multiply it by a percentage.</div>
          <ul>
            <li>Example: I want my Bad Debt to be 2% of my Gross Potential Rent</li>
            <li>Formula: Select the correct GL Account multiplied by 2%</li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(1)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(1)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Cloning Existing Historical Row(s)</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>Allows you to take the historical vendor rows and use them to create new working rows with the same vendor name and same dollar amounts or make a calculation based on those historical dollar amounts.</div>
          <ul>
            <li>Example: I want the historical vendor working rows to be created and increased by 10%</li>
            <li>Formula: Historical Vendor rows with additional calculations multiplied by 10%</li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(2)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(2)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong># of Budgeted Occupied Units, Vacant Units, Total Units, Move Ins, Move Outs, Renewals</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>Allows you to take the number budgeted for the item selected multiplied by an amount multiplied by a percentage</div>
          <br/>
          <div>Here are where these numbers are pulling from:</div>
          <ul>
            <li>Occupied Units - Occupancy > Top Summary Section > Ending Occupied Units</li>
            <li>Vacant Units - Occupancy > Top Summary Section > Ending Vacant Units</li>
            <li>Total Units - Occupancy > Top Summary Section > Total Units</li>
            <li>Move-Ins - Occupancy > Move Ins/Outs tab > Top Summary Section > Total Move Ins</li>
            <li>Move-Outs - Occupancy > Move Ins/Outs tab > Top Summary Section > Total Move Outs</li>
            <li>Renewals - Occupancy > Move Ins/Outs tab > Top Summary Section > Projected # Renewed</li>
            <br/>
            <li>Example: Pet Fees: I want to charge my move ins $100 for pet owners, but only 50% of my residents own a pet.</li>
            <li>Formula: Move In's x $100 x 50%</li>
            <br/>
            <li>Example 2: Renewal Fees: I want to charge $65 for every renewal</li>
            <li>Formula: Renewals x $65 x 100%</li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(3)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(3)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Starting with nothing, current year budget, or prior year actuals</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>Allows you to begin with the number options above and increase/decrease it by an amount or percentage.</div>
          <ul>
            <li>Example: I want to populate every row with $250</li>
            <li>Formula: Starting with nothing, increase by $250</li>
            <br/>
            <li>Example: I want to take last year's actuals and increase them by 4%</li>
            <li>Formula: Starting with Last Year Actuals - Monthly multiply by 4%</li>
            <br/>
            <li>Example: I want to clear the row back to zeros</li>
            <li>Formula: Starting with nothing, multiply by zero</li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(4)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(4)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Management Fee</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>Allows you to use your Management Fee calculation. You will only use this calculation type for Management Fee expense.</div>
          <div>You will only be able to select a Management fee if these two items are true:</div>
          <ol>
            <li>The calculation method is entirely based on Account Type = GL Account</li>
            <li>The calculation method accounting basis matches your budget accounting basis</li>
          </ol>
          <br/>
          <ul>
            <li>Example: I want to retrieve a management fee that can be applied to this row</li>
            <li>Formula: Select the Management Fee</li>
            <li class="no-bullet">
              <ul>
                <li>The system will look at the budgeted amounts for each GL Account set up in Management Fees and apply the percentage.</li>
              </ul>
            </li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(5)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(5)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Turnover Costs</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>Allows you to calculation your "turn cost" which is your cost for this GL Account every time someone moves out.</div>
          <div>There are two ways to calculate this number:</div>
          <ul>
            <li>
              Based on prior year actuals divided by the number of move outs in the prior year.
              <br/>
              If your prior year expense was $12,000 and there were 12 move outs last year, than your turn cost would be $1,000.
            </li>
            <li>Based on your manually entered number. If you know how much the cost of this turn is, you can enter the amount.</li>
          </ul>
          <br/>
          <div>Once the turn cost has been established, the formula will take that amount and multiply it by the number of estimated move outs.</div>
          <div>The number of move outs can be found in Occupancy > Move Ins/Outs tab > Top Summary Section > Total Move Outs</div>
          <ul>
            <li>Example: I want to calculate the cost of a turn by looking at my Prior Year expenses annualized.</li>
            <li>Formula: (PY Annual Expenses divided by PY Move outs) multiplied by the number of move outs.</li>
            <li>In the above example ($1,000) multiplied by the number of move outs in each month.</li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Prefer to watch a video? <a @click="openExternalTopic('BudgetsPro GL Calculations')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Want to see all BudgetsPro help articles? <a @click="openExternalTopic('BudgetsPro')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
  import HelpMixin from '@/mixins/Help.js'
  export default {
    name: 'BudgetCalculationsHelp',
    mixins: [HelpMixin]
  }
</script>

<style lang="scss" scoped>
  .no-bullet {
    list-style-type: none;
  }
  .icon {
      order: 0;
  }
  .header {
      order: 1;
  }
  img {
    max-width: 650px;
  }
  img.small {
    width: 35px;
  }
  .help-container {
      border-bottom: 1px solid var(--v-resGrey-lighten1)
  }
  .help-content div,
  .help-content li {
      margin-top:10px !important;
      margin-bottom:10px !important;
  }
  .banner {
    text-align: center;
    background-color: var(--v-resGrey-lighten1);
    display: block;
  }
  .pointer {
      cursor: pointer !important;
  }
  .separator {
    display: flex;
    align-items: center;
    text-align: center;
  }
  .separator::before,
  .separator::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--v-resGrey-darken1);
  }

  .separator:not(:empty)::before {
    margin-right: .25em;
  }

.separator:not(:empty)::after {
  margin-left: .25em;
}
</style>
